import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Language master (B4.10). Guide/driver/spoken-language taxonomy used by
 * Activity (guide language) and Transfer (driver language). Seeded from
 * ISO-639-1 codes; admin can extend.
 */

export const LanguageCodeSchema = z
  .string()
  .trim()
  .regex(/^[a-z]{2,3}$/, 'ISO-639-1 code is 2-3 lowercase letters')
  .transform((s) => s.toLowerCase());

export const LanguageListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  nativeName: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type LanguageListItem = z.infer<typeof LanguageListItemSchema>;

export const LanguageListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type LanguageListQuery = z.infer<typeof LanguageListQuerySchema>;

export const LanguageListResponseSchema = paginatedResponseSchema(LanguageListItemSchema);
export type LanguageListResponse = z.infer<typeof LanguageListResponseSchema>;

export const LanguageDetailSchema = LanguageListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type LanguageDetail = z.infer<typeof LanguageDetailSchema>;

const LanguageCoreFieldsSchema = z.object({
  code: LanguageCodeSchema,
  name: z.string().trim().min(1).max(120),
  nativeName: z.string().trim().max(120).nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateLanguageRequestSchema = LanguageCoreFieldsSchema;
export type CreateLanguageRequest = z.infer<typeof CreateLanguageRequestSchema>;

export const UpdateLanguageRequestSchema = LanguageCoreFieldsSchema.partial();
export type UpdateLanguageRequest = z.infer<typeof UpdateLanguageRequestSchema>;
